import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { CustomerRatingsFragmentFragmentDoc } from '@packages/gql/generated/shopping/CustomerRatingsFragmentFragmentDoc';
import { RatingSeoResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/RatingSeoResultFragmentFragmentDoc';
import { LazyRender } from '@packages/modules';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Link,
  RatingIcons,
  Stack,
  Typography,
} from '@packages/shared';
import { usePathname, useSearchParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { SlotBox } from '../SlotBox';
import { AddRating } from './AddRating';
import { CustomerRatingsFallback } from './CustomerRatingsFallback';
import { RatingInfo } from './RatingInfo';
import { RatingTags } from './RatingTags';
import { Review } from './Review';
import { ReviewStatistic } from './ReviewStatistic';

/* GraphQL */ `
  fragment RatingSeoResultFragment on RatingResult {
    product {
      avgRating
      reviewCount
      reviews {
        ...ReviewFragment
      }
      tags {
        ...RatingTagsFragment
      }
      reviewStatistics {
        ...ReviewStatisticFragment
      }
      sku
    }
    verifiedRatingsHintLayerId
  }
`;

/* GraphQL */ `
  fragment CustomerRatingsFragment on Product {
    sku
    availability {
      state
    }
    ...AddRatingFragment
    ...RatingTagSkuFragment
  }
`;

type CustomerRatingsProps = {
  /**
   * RatingResult product data
   */
  ratingResultData: FragmentType<typeof RatingSeoResultFragmentFragmentDoc>;
  /**
   * ProductData for glycerin submitRating event
   */
  productData: FragmentType<typeof CustomerRatingsFragmentFragmentDoc>;
};

/**
 * Customer ratings slot component displayed in the detailview, containing statistics, tag lists for filtering, max 3 reviews and the add rating button
 */
export const CustomerRatings = ({ ratingResultData, productData }: CustomerRatingsProps) => {
  const { product: ratingResultproduct, verifiedRatingsHintLayerId } = getFragmentData(
    RatingSeoResultFragmentFragmentDoc,
    ratingResultData,
  );
  const product = getFragmentData(CustomerRatingsFragmentFragmentDoc, productData);

  const { formatMessage } = useIntl();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const createLink = () => {
    const newSearchParams = new URLSearchParams(searchParams?.toString());
    newSearchParams.set(
      'sku',
      product.availability.state !== 'UNSELECTED' ? product.sku : searchParams?.get('sku') || '',
    );
    return `${pathname}/rating?${newSearchParams.toString()}`;
  };

  if (!ratingResultproduct) {
    return null;
  }
  const { avgRating, reviewCount, reviews, tags, reviewStatistics } = ratingResultproduct;

  return (
    <>
      <Typography
        variant="h2"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 2, typography: ['h4', 'h4', 'h3'] }}
        component="header"
      >
        {formatMessage({
          id: 'detailview.grid.headline.ratings',
          defaultMessage: 'Kundenbewertungen',
        })}
        <RatingInfo verifiedRatingsHintLayerId={verifiedRatingsHintLayerId} />
      </Typography>
      <SlotBox>
        <LazyRender
          key={`${ratingResultproduct.sku}-customer-ratings`}
          fallback={
            <CustomerRatingsFallback
              // previewReviewsCount={reviews.length}
              previewReviewsCount={1}
              reviewsCount={reviewCount}
            />
          }
          options={{ threshold: 0.1 }}
        >
          <Grid
            container
            columnSpacing={{ xs: 2, sm: 2, md: 2, lg: 5.5 }}
            rowSpacing={1.5}
            id="customer-ratings"
          >
            <Grid
              item
              xs={12}
              md={6}
              lg={5}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              {reviewCount > 0 && (
                <Card>
                  <CardHeader
                    sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
                    title={formatMessage({
                      id: 'ratingsOverview.title',
                      defaultMessage: 'Bewertungsübersicht',
                    })}
                  />
                  <CardContent>
                    <RatingIcons avgRating={avgRating} reviewCount={reviewCount} sx={{ mb: 0.5 }} />
                    <Stack spacing={0.5} mb={3}>
                      {reviewStatistics.map((rs, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <ReviewStatistic reviewStatisticData={rs} key={index} />
                      ))}
                    </Stack>
                    <RatingTags ratingTagsData={tags} skuData={product} />
                  </CardContent>
                </Card>
              )}
              <AddRating productData={product} />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              lg={7}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
            >
              {reviewCount > 0 && (
                <>
                  {reviews.map((review, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Review key={index} reviewData={review} />
                  ))}
                  <Link
                    href={createLink()}
                    sx={{ alignSelf: 'center', mt: ['auto', 'auto', 1.5] }}
                    noLinkStyle
                  >
                    <Button>
                      {formatMessage(
                        {
                          defaultMessage: 'Alle Bewertungen ({ratingCount}) anzeigen',
                          id: 'ratings.show-all',
                        },
                        { ratingCount: reviewCount },
                      )}
                    </Button>
                  </Link>
                </>
              )}
            </Grid>
          </Grid>
        </LazyRender>
      </SlotBox>
    </>
  );
};
